import { SiteShell } from "@/components/site/SiteShell";
import { PageHero } from "@/components/site/PageHero";
import { SEO, breadcrumbSchema } from "@/components/site/SEO";
import { INDUSTRIES } from "@/lib/siteData";
import { Plane, ShoppingBag, Car, Stethoscope, Trophy, Hotel, Building, HeartHandshake, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ROUTE_SEO } from "@/lib/routeSeo";
import { Link } from "react-router-dom";

const ICONS = { Plane, ShoppingBag, Car, Stethoscope, Trophy, Hotel, Building, HeartHandshake };

export default function Industries() {
  return (
    <SiteShell>
      <SEO title={ROUTE_SEO["/industries"].title} description={ROUTE_SEO["/industries"].meta} path="/industries" jsonLd={breadcrumbSchema([{name: "Home", path: "/"}, {name: "Industries"}])} />
      <PageHero kicker="Sectors" title={ROUTE_SEO["/industries"].h1} subtitle="Decade-deep experience across the sectors that matter - hospitality, retail, automotive, healthcare, sport, corporate and documentary." breadcrumb={[{name: "Home", path: "/"}, {name: "Industries"}]} />
      <section className="section-pad">
        <div className="container-site grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {INDUSTRIES.map((ind) => {
            const Icon = ICONS[ind.icon] || Building;
            return (
              <div key={ind.name} className="rounded-2xl border border-border bg-card p-7 hover:bg-[hsl(225,18%,9%)] transition-colors">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(225,16%,14%)] border border-border text-[hsl(var(--primary))]"><Icon className="h-5 w-5" /></span>
                <h3 className="mt-5 font-display text-xl uppercase tracking-[0.02em] text-foreground">{ind.name}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{ind.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Hotels & Lodges + Marketing Agencies - dedicated funnel CTAs */}
        <div className="container-site mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Link
            to="/for/hotels-and-lodges"
            data-testid="industries-hotels-cta"
            className="group relative block overflow-hidden rounded-2xl border border-[hsl(var(--primary))/0.4] bg-[hsl(225,18%,8%)] p-8 hover:border-[hsl(var(--primary))/0.7] transition-colors"
          >
            <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[hsl(var(--primary))/0.12] blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3"><Sparkles className="h-4 w-4 text-[hsl(var(--primary))]" /><span className="kicker">For Hotels & Lodges</span></div>
              <h3 className="font-display uppercase text-foreground text-2xl sm:text-3xl leading-[0.98] tracking-[0.01em]">Luxury Hospitality <span className="text-[hsl(var(--primary))]">Visibility Engine™</span></h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-md">Cinematic property films + SEO + AI search optimisation. Engineered to drive direct bookings.</p>
              <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-foreground group-hover:text-[hsl(var(--primary))] transition-colors">See hotels page <ArrowRight className="h-3.5 w-3.5" /></span>
            </div>
          </Link>

          <Link
            to="/for/marketing-agencies"
            data-testid="industries-agencies-cta"
            className="group relative block overflow-hidden rounded-2xl border border-[hsl(var(--primary))/0.4] bg-[hsl(225,18%,8%)] p-8 hover:border-[hsl(var(--primary))/0.7] transition-colors"
          >
            <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[hsl(var(--primary))/0.12] blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3"><Sparkles className="h-4 w-4 text-[hsl(var(--primary))]" /><span className="kicker">For Marketing & Ad Agencies</span></div>
              <h3 className="font-display uppercase text-foreground text-2xl sm:text-3xl leading-[0.98] tracking-[0.01em]">Your agency&apos;s <span className="text-[hsl(var(--primary))]">unfair creative edge.</span></h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-md">Cinematic + AI scale + SEO via OptimaGrowth. Free AI concept reel + treatment in 72 hours.</p>
              <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-foreground group-hover:text-[hsl(var(--primary))] transition-colors">See agency page <ArrowRight className="h-3.5 w-3.5" /></span>
            </div>
          </Link>
        </div>

        <div className="container-site mt-16 text-center">
          <Button asChild className="h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-6"><Link to="/contact">Talk to a senior producer <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
        </div>
      </section>
    </SiteShell>
  );
}
